import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  styled,
  useTheme
} from "@mui/material";
import { COLORS } from "../constants/colors";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { ResourceDrawer } from "../components/ui/resourceDrawer/ResourceDrawer";
import { OLD_APP_LOGIN_URL } from "../constants/potato";
import MenuIcon from "@mui/icons-material/Menu";

const queryClient = new QueryClient();

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: 0,
    marginTop: "65px",
    overflowX: "auto",
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
          }),
          marginLeft: "20px"
        }
      }
    ]
  })
);

export default function Root() {
  const [cookies] = useState(() => {
    const cookieObj = {};
    document.cookie.split(";").forEach((cookie) => {
      const [key, value] = cookie.split("=");
      cookieObj[key.trim()] = decodeURIComponent(value);
    });
    return cookieObj;
  });

  const [isOpen, setIsOpen] = useState(true);

  const theme = useTheme();

  const toggleDrawer = () => {
    setIsOpen((prev) => !prev);
  };

  const email = cookies.userEmail;

  const isLoggedIn =
    Boolean(email) || process.env.REACT_APP_ENVIRONMENT === "test";

  useEffect(() => {
    if (!isLoggedIn) {
      window.location.replace(OLD_APP_LOGIN_URL);
    }
  }, [isLoggedIn]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        theme={createTheme({
          typography: { fontSize: "1em" },
          palette: {
            primary: { main: COLORS.OFFSET },
            secondary: { main: COLORS.WARM_GRAY }
          }
        })}
      >
        <Box
          sx={{ display: "flex", width: "100%", height: "100%", gap: "8px" }}
        >
          <CssBaseline />
          <AppBar
            position="fixed"
            open={isOpen}
            sx={{
              marginLeft: isOpen ? "255px" : 0,
              boxShadow: "none"
            }}
          >
            <Toolbar>
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  transition: theme.transitions.create("margin", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen
                  }),
                  marginLeft: isOpen ? "255px" : 0,
                  alignItems: "center"
                }}
              >
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={toggleDrawer}
                  edge="start"
                  sx={[
                    {
                      mr: 2
                    },
                    isOpen && { display: "none" }
                  ]}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>
          <ResourceDrawer
            email={email}
            isOpen={isOpen}
            toggleIsOpen={toggleDrawer}
          />
          <Main open={isOpen}>
            <Outlet />
          </Main>
        </Box>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
